import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const TeacherAttendance = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);

  const [totalPresent, setTotalPresent] = useState(0);
  const [totalAbsent, setTotalAbsent] = useState(0);
  const [totalLeave, setTotalLeave] = useState(0);

  const teacher = JSON.parse(localStorage.getItem("teacher"));
  const teacherId = teacher?.id;
  const sessionId = localStorage.getItem("currentSessionId");

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teacherAttendances/getAttendanceByTeacherId/${teacherId}`,
          { params: { sessionId } }
        );
        setAttendanceData(response.data || []);
        calculateTotals(response.data || []);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };
    fetchAttendance();
  }, [teacherId, sessionId]);

  const calculateTotals = (data) => {
    let present = 0,
      absent = 0,
      leave = 0;

    data.forEach((record) => {
      const teacherRecord = record.teachers.find(t => t.teacherId === teacherId);
      if (!teacherRecord) return;
      if (teacherRecord.status === "Present") present++;
      else if (teacherRecord.status === "Absent") absent++;
      else if (teacherRecord.status === "Leave") leave++;
    });

    setTotalPresent(present);
    setTotalAbsent(absent);
    setTotalLeave(leave);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else setCurrentMonth(currentMonth - 1);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else setCurrentMonth(currentMonth + 1);
  };

  const handleDateClick = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const record = attendanceData.find(r =>
      new Date(r.date).toLocaleDateString() === date.toLocaleDateString()
    );

    if (record) {
      const teacherRecord = record.teachers.find(t => t.teacherId === teacherId);
      if (teacherRecord) {
        setSelectedAttendance(teacherRecord);
        setSelectedDate(date.toLocaleDateString());
        setShowModal(true);
      }
    }
  };

  const handleCloseModal = () => setShowModal(false);

  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthDays = [];

    // Weekday headers
    daysOfWeek.forEach((day, i) => {
      monthDays.push(
        <div key={`header-${i}`} className="calendar-header">
          {day}
        </div>
      );
    });

    // Empty tiles
    for (let i = 0; i < firstDay; i++) {
      monthDays.push(<div key={`empty-${i}`} className="calendar-tile empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();

      const record = attendanceData.find(r =>
        new Date(r.date).toDateString() === date.toDateString()
      );

      let statusClass = "";
      if (record) {
        const teacherRecord = record.teachers.find(t => t.teacherId === teacherId);
        if (teacherRecord) {
          if (teacherRecord.status === "Present") statusClass = "present";
          else if (teacherRecord.status === "Absent") statusClass = "absent";
          else if (teacherRecord.status === "Leave") statusClass = "leave";
          else if (teacherRecord.status === "Late") statusClass = "late";
        }
      } else if (isToday) {
        statusClass = "today";
      }

      monthDays.push(
        <div
          key={day}
          className={`calendar-tile ${statusClass}`}
          onClick={() => handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return monthDays;
  };

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  return (
    <div className="calendar-container">
      <div className="calendar-title">Teacher Attendance</div>

      <div className="calendar-nav">
        <button onClick={handlePrevMonth}>◀</button>
        <div className="calendar-month">{monthNames[currentMonth]} {currentYear}</div>
        <button onClick={handleNextMonth}>▶</button>
      </div>

      <div className="calendar-grid">{generateCalendarDays()}</div>

      <div className="calendar-summary">
        <div className="summary-card present">
          <div>Present</div>
          <div>{totalPresent}</div>
        </div>
        <div className="summary-card absent">
          <div>Absent</div>
          <div>{totalAbsent}</div>
        </div>
        <div className="summary-card leave">
          <div>Leave</div>
          <div>{totalLeave}</div>
        </div>
      </div>

      {showModal && selectedAttendance && (
        <div className="modal-overlay">
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <div>Attendance Details</div>
              <button onClick={handleCloseModal}>✕</button>
            </div>
            <div>Date: {selectedDate}</div>
            <div>Status: <span className={selectedAttendance.status.toLowerCase()}>{selectedAttendance.status}</span></div>
            {selectedAttendance.remark && <div>Remark: {selectedAttendance.remark}</div>}
          </div>
        </div>
      )}

      {/* CSS for the component */}
      <style jsx>{`
        .calendar-container {
          width: 100%;
          max-width: 400px;
          margin: 20px auto;
          padding: 16px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
        }
        .calendar-title {
          text-align: center;
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 12px;
        }
        .calendar-nav {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        }
        .calendar-nav button {
          padding: 4px 8px;
          border-radius: 6px;
          background: #EDF2F7;
          border: none;
          cursor: pointer;
        }
        .calendar-month {
          font-weight: 600;
          font-size: 16px;
        }
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }
        .calendar-header {
          text-align: center;
          font-weight: 600;
          font-size: 14px;
          color: #4A5568;
        }
        .calendar-tile {
          text-align: center;
          padding: 8px 0;
          border-radius: 6px;
          cursor: pointer;
          border: 1px solid #E2E8F0;
          transition: all 0.2s;
        }
        .calendar-tile:hover {
          background-color: #E2E8F0;
        }
        .calendar-tile.present { background-color: #38A169; color: #fff; }
        .calendar-tile.absent { background-color: #F56565; color: #fff; }
        .calendar-tile.leave { background-color: #ECC94B; color: #fff; }
        .calendar-tile.late { background-color: #ED8936; color: #fff; }
        .calendar-tile.today { background-color: #63B3ED; color: #fff; }

        .calendar-summary {
          display: flex;
          justify-content: space-between;
          margin-top: 16px;
        }
        .summary-card {
          flex: 1;
          text-align: center;
          padding: 8px;
          border-radius: 6px;
          margin-right: 4px;
          font-weight: 600;
        }
        .summary-card.present { background: #C6F6D5; color: #2F855A; }
        .summary-card.absent { background: #FED7D7; color: #C53030; }
        .summary-card.leave { background: #FEFCBF; color: #D69E2E; margin-right: 0; }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 999;
        }
        .modal-content {
          background: #fff;
          border-radius: 8px;
          padding: 16px;
          max-width: 300px;
          width: 90%;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-weight: 600;
          font-size: 16px;
        }
        .present { color: #38A169; }
        .absent { color: #F56565; }
        .leave { color: #ECC94B; }
        .late { color: #ED8936; }
      `}</style>
    </div>
  );
};

export default TeacherAttendance;
